import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Globe, School, GraduationCap, BookOpen, Presentation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  PlayStoreIcon,
  AppStoreIcon,
  PLAY_STORE_URL,
  APP_STORE_URL,
} from '@/components/StoreIcons';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const servicesRef = useRef(null);
  const closeServicesTimeoutRef = useRef(null);
  const location = useLocation();

  const isServicesActive =
    location.pathname.startsWith('/services') &&
    location.pathname !== '/services/ai-chatbot';
  const isAIChatbotActive = location.pathname === '/services/ai-chatbot';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (closeServicesTimeoutRef.current) {
        clearTimeout(closeServicesTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;

    const syncBodyScrollLock = () => {
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      document.body.style.overflow = mobile ? 'hidden' : '';
    };

    syncBodyScrollLock();
    window.addEventListener('resize', syncBodyScrollLock);

    return () => {
      window.removeEventListener('resize', syncBodyScrollLock);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    // { name: 'Features', path: '/services/features' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceCategories = [
    {
      label: 'School',
      items: [
        {
          name: 'School Management Software',
          description: 'Complete management software for schools',
          path: '/services/features',
          icon: School,
          testId: 'features',
        },
        {
          name: 'School Website',
          description: 'Complete website solution for schools',
          path: '/services/school-website',
          icon: Globe,
          testId: 'school-website',
        },
      ],
    },
    {
      label: 'College',
      items: [
        {
          name: 'College Management Software',
          description: 'Complete management software for colleges',
          path: '/services/college-features',
          icon: GraduationCap,
          testId: 'college-features',
        },
        {
          name: 'College Website',
          description: 'Complete website solution for colleges',
          path: '/services/college-website',
          icon: Globe,
          testId: 'college-website',
        },
      ],
    },
    {
      label: 'Learning',
      items: [
        {
          name: 'Learning Management Solution',
          description: 'Digital courses, assessments, and progress tracking',
          path: '/services/learning-management',
          icon: BookOpen,
          testId: 'learning-management',
        },
      ],
    },
    {
      label: 'Coaching',
      items: [
        {
          name: 'Coaching Management Solution',
          description: 'Batches, fees, tests, and admissions for coaching institutes',
          path: '/services/coaching',
          icon: Presentation,
          testId: 'coaching',
        },
      ],
    },
  ];

  const openServicesMenu = () => {
    if (closeServicesTimeoutRef.current) {
      clearTimeout(closeServicesTimeoutRef.current);
      closeServicesTimeoutRef.current = null;
    }
    setIsServicesOpen(true);
  };

  const closeServicesMenuWithDelay = () => {
    if (closeServicesTimeoutRef.current) {
      clearTimeout(closeServicesTimeoutRef.current);
    }
    closeServicesTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
      closeServicesTimeoutRef.current = null;
    }, 160);
  };

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav shadow-sm' : 'bg-white/50'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo-link"
            className="flex items-center space-x-2"
          >
            <img
              src="/logo.webp"
              alt="VibhumEduSys logo"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className="text-xl font-heading font-bold text-navy">
              VibhumEduSys
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.slice(0, 1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-emerald font-semibold'
                    : 'text-slate-700 hover:text-emerald'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <div
              ref={servicesRef}
              className="relative"
              onMouseEnter={openServicesMenu}
              onMouseLeave={closeServicesMenuWithDelay}
            >
              <button
                type="button"
                data-testid="nav-link-services"
                className={`inline-flex items-center gap-1 text-base font-medium transition-colors ${
                  isServicesActive
                    ? 'text-emerald font-semibold'
                    : 'text-slate-700 hover:text-emerald'
                }`}
                onClick={() => setIsServicesOpen((prev) => !prev)}
                aria-expanded={isServicesOpen}
                aria-haspopup="menu"
              >
                Services
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isServicesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`fixed left-1/2 top-20 w-[min(64rem,calc(100vw-2rem))] -translate-x-1/2 pt-2 transition-all duration-200 ${
                  isServicesOpen
                    ? 'pointer-events-auto translate-y-0 opacity-100'
                    : 'pointer-events-none -translate-y-1 opacity-0'
                }`}
                role="menu"
                onMouseEnter={openServicesMenu}
                onMouseLeave={closeServicesMenuWithDelay}
              >
                <div className="max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white p-5 shadow-lg [scrollbar-width:thin]">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-5 lg:grid-cols-4">
                    {serviceCategories.map((category) => (
                      <div key={category.label} className="min-w-0">
                        <p className="border-b border-slate-100 px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                          {category.label}
                        </p>
                        <div className="mt-2 space-y-1">
                          {category.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                data-testid={`services-link-${item.testId}`}
                                className={`block rounded-lg px-2 py-2.5 transition-colors ${
                                  location.pathname === item.path
                                    ? 'bg-emerald-light/20'
                                    : 'hover:bg-slate-50'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <span className="mt-0.5 shrink-0 rounded-md bg-emerald-light/20 p-1.5">
                                    <Icon className="h-4 w-4 text-emerald" />
                                  </span>
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold leading-snug text-slate-800">
                                      {item.name}
                                    </div>
                                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                      {item.description}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/services/ai-chatbot"
              data-testid="nav-link-ai-chatbot"
              className={`text-base font-medium transition-colors ${
                isAIChatbotActive
                  ? 'text-emerald font-semibold'
                  : 'text-slate-700 hover:text-emerald'
              }`}
            >
              AI Chatbot
            </Link>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-emerald font-semibold'
                    : 'text-slate-700 hover:text-emerald'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white/60 px-2 py-1">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-android-app-link"
                aria-label="Download on Google Play"
                title="Download on Google Play"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg transition-transform hover:scale-110 active:scale-95"
              >
                <PlayStoreIcon className="h-6 w-6" />
              </a>
              <div className="h-6 w-px bg-slate-200" />
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-ios-app-link"
                aria-label="Download on App Store"
                title="Download on App Store"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-navy transition-transform hover:scale-110 active:scale-95"
              >
                <AppStoreIcon className="h-6 w-6" />
              </a>
            </div>
            <Link to="/register">
              <Button
                data-testid="navbar-demo-button"
                className="bg-emerald hover:bg-emerald-light text-white font-semibold px-6"
              >
                Get Started Free
              </Button>
            </Link>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200/90 bg-white/70 text-navy shadow-sm backdrop-blur-sm transition hover:bg-white hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald/35 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            onClick={() => {
              setIsOpen((prev) => !prev);
              if (isOpen) {
                setIsMobileServicesOpen(false);
              }
            }}
            aria-expanded={isOpen}
            aria-controls="mobile-nav-panel"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <div
          id="mobile-nav-panel"
          data-testid="mobile-menu"
          aria-hidden={!isOpen}
          className={`md:hidden grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${
            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={`pb-6 pt-1 transition duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${
                isOpen
                  ? 'translate-y-0 opacity-100'
                  : 'pointer-events-none -translate-y-2 opacity-0'
              }`}
            >
              <div className="max-h-[min(calc(100vh-5.5rem),calc(100dvh-5.5rem))] overflow-y-auto overscroll-y-contain rounded-2xl border border-slate-200/90 bg-white/85 p-2 shadow-lg shadow-slate-900/[0.06] backdrop-blur-md [scrollbar-gutter:stable]">
              <nav className="space-y-1" aria-label="Mobile">
                {navLinks.slice(0, 1).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                    className={`block rounded-xl px-4 py-3 text-base font-medium transition-colors active:bg-slate-100/80 ${
                      location.pathname === link.path
                        ? 'bg-emerald-light/15 text-emerald font-semibold'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-emerald'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}

                <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50/80">
                  <button
                    type="button"
                    data-testid="mobile-nav-link-services"
                    className={`flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-base font-medium transition-colors active:bg-white/80 ${
                      isServicesActive
                        ? 'text-emerald font-semibold'
                        : 'text-slate-700 hover:text-emerald'
                    }`}
                    onClick={() => setIsMobileServicesOpen((prev) => !prev)}
                    aria-expanded={isMobileServicesOpen}
                    aria-controls="mobile-services-submenu"
                  >
                    <span>Services</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-300 ease-out ${
                        isMobileServicesOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  <div
                    id="mobile-services-submenu"
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      isMobileServicesOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="border-t border-slate-200/80 bg-white/95">
                        <div className="max-h-[min(420px,65vh)] overflow-y-auto overscroll-contain [scrollbar-width:thin]">
                          <p className="px-4 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                            School
                          </p>
                          <Link
                            to="/services/school-website"
                            data-testid="mobile-services-link-school-website"
                            className={`flex gap-3 px-4 py-3.5 transition-colors active:bg-slate-50 ${
                              location.pathname === '/services/school-website'
                                ? 'bg-emerald-light/15'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsMobileServicesOpen(false);
                            }}
                          >
                            <span className="mt-0.5 shrink-0 rounded-lg bg-emerald-light/20 p-2">
                              <Globe className="h-4 w-4 text-emerald" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold leading-snug text-slate-800">
                                School Website
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                Complete website solution for schools
                              </p>
                            </div>
                          </Link>

                          <div className="mx-4 h-px bg-slate-100" />

                          <Link
                            to="/services/features"
                            data-testid="mobile-services-link-features"
                            className={`flex gap-3 px-4 py-3.5 transition-colors active:bg-slate-50 ${
                              location.pathname === '/services/features'
                                ? 'bg-emerald-light/15'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsMobileServicesOpen(false);
                            }}
                          >
                            <span className="mt-0.5 shrink-0 rounded-lg bg-emerald-light/20 p-2">
                              <School className="h-4 w-4 text-emerald" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold leading-snug text-slate-800">
                                School Management Software
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                Complete management software for schools
                              </p>
                            </div>
                          </Link>

                          <div className="mx-4 my-2 h-px bg-slate-200" />

                          <p className="px-4 pt-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                            College
                          </p>
                          <Link
                            to="/services/college-website"
                            data-testid="mobile-services-link-college-website"
                            className={`flex gap-3 px-4 py-3.5 transition-colors active:bg-slate-50 ${
                              location.pathname === '/services/college-website'
                                ? 'bg-emerald-light/15'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsMobileServicesOpen(false);
                            }}
                          >
                            <span className="mt-0.5 shrink-0 rounded-lg bg-emerald-light/20 p-2">
                              <Globe className="h-4 w-4 text-emerald" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold leading-snug text-slate-800">
                                College Website
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                Complete website solution for colleges
                              </p>
                            </div>
                          </Link>

                          <div className="mx-4 h-px bg-slate-100" />

                          <Link
                            to="/services/college-features"
                            data-testid="mobile-services-link-college-features"
                            className={`flex gap-3 px-4 py-3.5 transition-colors last:pb-4 active:bg-slate-50 ${
                              location.pathname === '/services/college-features'
                                ? 'bg-emerald-light/15'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsMobileServicesOpen(false);
                            }}
                          >
                            <span className="mt-0.5 shrink-0 rounded-lg bg-emerald-light/20 p-2">
                              <GraduationCap className="h-4 w-4 text-emerald" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold leading-snug text-slate-800">
                                College Management Software
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                Complete management software for colleges
                              </p>
                            </div>
                          </Link>

                          <div className="mx-4 my-2 h-px bg-slate-200" />

                          <p className="px-4 pt-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                            Coaching
                          </p>
                          <Link
                            to="/services/coaching"
                            data-testid="mobile-services-link-coaching"
                            className={`flex gap-3 px-4 py-3.5 transition-colors last:pb-4 active:bg-slate-50 ${
                              location.pathname === '/services/coaching'
                                ? 'bg-emerald-light/15'
                                : 'hover:bg-slate-50'
                            }`}
                            onClick={() => {
                              setIsOpen(false);
                              setIsMobileServicesOpen(false);
                            }}
                          >
                            <span className="mt-0.5 shrink-0 rounded-lg bg-emerald-light/20 p-2">
                              <Presentation className="h-4 w-4 text-emerald" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold leading-snug text-slate-800">
                                Coaching Management Solution
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                Batches, fees, tests, and admissions for coaching institutes
                              </p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/services/ai-chatbot"
                  data-testid="mobile-nav-link-ai-chatbot"
                  className={`block rounded-xl px-4 py-3 text-base font-medium transition-colors active:bg-slate-100/80 ${
                    isAIChatbotActive
                      ? 'bg-emerald-light/15 text-emerald font-semibold'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-emerald'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  AI Chatbot
                </Link>

                {navLinks.slice(1).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                    className={`block rounded-xl px-4 py-3 text-base font-medium transition-colors active:bg-slate-100/80 ${
                      location.pathname === link.path
                        ? 'bg-emerald-light/15 text-emerald font-semibold'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-emerald'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}

                <div className="pt-2">
                  <Link to="/register" onClick={() => setIsOpen(false)} className="block">
                    <Button
                      data-testid="mobile-demo-button"
                      className="h-12 w-full rounded-xl bg-emerald text-base font-semibold text-white shadow-sm shadow-emerald/20 hover:bg-emerald-light"
                    >
                      Get Started Free
                    </Button>
                  </Link>
                </div>

                <div className="flex items-center justify-center gap-3 pt-3">
                  <span className="text-sm font-medium text-slate-500">
                    Get the app
                  </span>
                  <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white/60 px-2 py-1">
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mobile-android-app-link"
                      aria-label="Download on Google Play"
                      title="Download on Google Play"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg transition-transform active:scale-95"
                    >
                      <PlayStoreIcon className="h-6 w-6" />
                    </a>
                    <div className="h-6 w-px bg-slate-200" />
                    <a
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mobile-ios-app-link"
                      aria-label="Download on App Store"
                      title="Download on App Store"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-navy transition-transform active:scale-95"
                    >
                      <AppStoreIcon className="h-6 w-6" />
                    </a>
                  </div>
                </div>
              </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
